import React from "react";
import { gql } from "@apollo/client";
import { useRemoteHrisSftpCsvSidebarQuery } from "./RemoteHrisSftpCsvDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import { format, parseISO } from "date-fns";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import FileOutlineIcon from "@src/lib/icons/18px/file-outline";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";
import { Button } from "@src/lib/ui/Button";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
} from "@src/lib/ui/QueryTabs";
import Table, { FuseSearchConfig } from "@src/lib/ui/Table";

type Props = {
  rowId: string; // Format: "orgSftpIntegrationId:fileName"
  showHeader?: boolean;
};

const RemoteHrisSftpCsvDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  // Parse the rowId to get orgSftpIntegrationId and fileName
  const [orgSftpIntegrationId, fileName] = rowId.split(":");

  const { data, loading } = useRemoteHrisSftpCsvSidebarQuery({
    variables: {
      orgSftpIntegrationId,
      fileName: decodeURIComponent(fileName),
    },
    skip: !orgSftpIntegrationId || !fileName,
  });

  const file = data?.RemoteHrisSftpCsv;

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-7">Loading...</div>
      </div>
    );
  }

  if (!file) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-gray-7">File not found</div>
      </div>
    );
  }

  const tabKeys = ["details", "content"] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    content: "CSV content",
  };

  const downloadCsv = () => {
    // Create CSV content from the csvContent array
    if (!file.csvContent || file.csvContent.length === 0) return;

    const headers = Object.keys(file.csvContent[0]);
    const csvRows = [headers.join(",")];

    file.csvContent.forEach((row) => {
      const values = headers.map((header) => row[header] || "");
      csvRows.push(values.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", file.fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={detailsItems} />,
    content: () => {
      if (!file.csvContent || file.csvContent.length === 0) {
        return (
          <div className="p-4">
            <div className="text-gray-7">No CSV content available</div>
          </div>
        );
      }

      const headers = Object.keys(file.csvContent[0]);
      type Column = (typeof headers)[number];
      const allColumns = headers as Column[];

      // Add index to each row for unique IDs
      const rowsWithIndex = file.csvContent.map((row, index) => ({
        ...row,
        __index: index,
      }));

      const renderFns: Record<
        Column,
        (row: Record<string, unknown>) => React.ReactNode
      > = Object.fromEntries(
        headers.map((header) => [
          header,
          (row: Record<string, unknown>) => String(row[header] || ""),
        ]),
      ) as Record<Column, (row: Record<string, unknown>) => React.ReactNode>;

      const columnTitles: Record<Column, string> = Object.fromEntries(
        headers.map((header) => [header, header]),
      ) as Record<Column, string>;

      // Create Fuse search config that searches across all column values
      const fuseSearchConfig: FuseSearchConfig<Record<string, unknown>> = {
        threshold: 0.3,
        keys: headers.map((header) => ({
          name: header,
          getFn: (row) => String(row[header] || ""),
        })),
      };

      return (
        <Table<Record<string, unknown>, Column>
          title="CSV content"
          allItems={rowsWithIndex}
          allColumns={allColumns}
          itemId={(row) => String(row.__index ?? JSON.stringify(row))}
          columnTitles={columnTitles}
          renderFns={renderFns}
          showCount={true}
          fuseSearchConfig={fuseSearchConfig}
          columnSelection={{
            enabled: true,
            defaultSelectedColumns: allColumns,
          }}
          sidebarConfig={{
            tableId: "remote-hris-sftp-csv-content",
            sidebarType: DetailSidebarType.RemoteHrisSftpCsv,
            enabled: false, // Don't enable sidebar navigation, just use for scoped search
          }}
          noDataEmptyState="No CSV content available"
        />
      );
    },
  };

  const detailsItems: InfoTableItem[] = [
    {
      label: "File name",
      value: file.fileName,
    },
    {
      label: "Uploaded at",
      value: file.uploadedAt
        ? format(parseISO(file.uploadedAt), "M/d/yyyy h:mm a")
        : "--",
    },
    {
      label: "Size",
      value: `${(file.size / 1024).toFixed(2)} KB`,
    },
    {
      label: "Rows",
      value: file.csvContent?.length?.toString() || "0",
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={file.fileName}
        icon={<FileOutlineIcon className="text-gray-9" />}
        sidebarType={DetailSidebarType.RemoteHrisSftpCsv}
      />
      <div className="px-4">
        <div className="flex gap-1">
          <Button
            variant="outline"
            onClick={downloadCsv}
            disabled={!file.csvContent || file.csvContent.length === 0}
          >
            <ArrowDownloadIcon className="text-muted-foreground" />
            Download CSV
          </Button>
        </div>
      </div>
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            defaultTab="details"
            queryParamKey="remote-hris-sftp-csv-tab"
          />
        </div>
      </SidebarContent>
    </div>
  );
};

gql`
  query RemoteHrisSftpCsvSidebar(
    $orgSftpIntegrationId: UUID!
    $fileName: String!
  ) {
    RemoteHrisSftpCsv(
      orgSftpIntegrationId: $orgSftpIntegrationId
      fileName: $fileName
    ) {
      fileName
      uploadedAt
      size
      csvContent
    }
  }
`;

export default RemoteHrisSftpCsvDetailSidebar;
